import { useCallback, useEffect, useState } from 'react';

/**
 * Lifecycle for the deployment-summary refresh.
 *
 * The summary step re-fetches deployment status so "Items that will be created" reflects the
 * CURRENT state of the target tenant(s). Two things must hold while that is in flight:
 *
 *  1. The refreshing state must be true on the FIRST render of the summary step. A passive
 *     `useEffect` that flips a `useState(false)` runs after the browser has already painted, so
 *     the user briefly sees stale content with Confirm enabled - exactly the guarantee the
 *     summary step is supposed to make. This hook derives the state during render instead, using
 *     React's documented "adjust state when a prop changes" pattern, so the very first summary
 *     render is already `refreshing`.
 *
 *  2. A failed refresh must NOT read as success. `dispatch(thunk())` resolves with a *rejected
 *     action* rather than rejecting, so a plain `.finally()` clears the flag and Confirm enables
 *     over data that was never refreshed. This hook calls `.unwrap()`, so a failure lands in
 *     `status: 'error'` and callers can block Confirm and offer a retry.
 */
export type SummaryRefreshStatus = 'idle' | 'refreshing' | 'error';

export interface UseSummaryRefreshResult {
  status: SummaryRefreshStatus;
  isRefreshing: boolean;
  /** True when the refresh failed; the summary is showing data that was never revalidated. */
  hasFailed: boolean;
  error: string | null;
  /** Re-runs the refresh; use for a "Try again" affordance in the error state. */
  retry: () => void;
}

/**
 * @param isActive Whether the summary step is currently shown.
 * @param refresh  Runs the refresh. MUST reject on failure - pass `.unwrap()`ed thunk results.
 */
export function useSummaryRefresh(
  isActive: boolean,
  refresh: () => Promise<unknown>,
): UseSummaryRefreshResult {
  // Derived during render: entering the summary step sets 'refreshing' before children render,
  // so there is no frame in which stale content is shown as settled.
  const [prevActive, setPrevActive] = useState(isActive);
  const [status, setStatus] = useState<SummaryRefreshStatus>(isActive ? 'refreshing' : 'idle');
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  if (prevActive !== isActive) {
    setPrevActive(isActive);
    setStatus(isActive ? 'refreshing' : 'idle');
    setError(null);
  }

  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;

    setStatus('refreshing');
    setError(null);

    refresh()
      .then(() => {
        if (!cancelled) setStatus('idle');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setStatus('error');
        setError(
          err instanceof Error
            ? err.message
            : typeof err === 'string'
              ? err
              : 'Could not refresh deployment status.',
        );
      });

    return () => {
      cancelled = true;
    };
    // `refresh` is re-created every render by callers; depending on it would loop. The refresh is
    // intentionally tied to entering the step and to an explicit retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    status,
    isRefreshing: status === 'refreshing',
    hasFailed: status === 'error',
    error,
    retry,
  };
}
